/**
 * Axis-aligned lookup tables (plan hypothesis T3).
 *
 * An affine RGB transform
 *
 *     out_c = M[0*3+c] * R + M[1*3+c] * G + M[2*3+c] * B + b[c]
 *
 * separates into three single-variable functions, one per input channel:
 *
 *     out_c = T_c0[R] + T_c1[G] + T_c2[B] + b[c]
 *     T_c,col[v] = M[col*3+c] * v
 *
 * Each table has exactly 256 entries because the input is an 8-bit channel.
 * A pixel therefore costs nine array lookups and nine additions, and zero
 * multiplications. That is the whole of T3.
 *
 * The accumulation order here is *identical* to the direct matrix evaluation in
 * `applyAffineInPlace`, so the two agree bit for bit (not merely to a
 * tolerance). `luts.test.ts` asserts that.
 *
 * ## T3b: the 32-cubed 3D LUT
 *
 * An axis-aligned table only works because the transform is affine. For a
 * nonlinear space such as CIE Lab the same idea needs a genuine 3D table:
 * `build3dLut` samples a function over a 32x32x32 grid (98 KB as `Float32`), and
 * `sample3dLut` interpolates trilinearly. That is hypothesis T3b, used by the
 * Lab-class presets.
 *
 * @module core/luts
 */

import type { Affine, Vec3 } from './types.ts';

/** Number of entries per axis-aligned table. One per 8-bit input value. */
export const LUT_SIZE = 256;

/** Side length of the 3D LUT. 32 gives 32768 entries. */
export const LUT3D_SIZE = 32;

/**
 * Build the nine tables for an affine.
 *
 * The returned array is indexed `[c * 3 + col]`, so channel `c` occupies slots
 * `3c`, `3c+1`, `3c+2` for the red, green and blue input respectively.
 */
export function buildLutTables(affine: Affine): Float64Array[] {
  const { M } = affine;
  const tables: Float64Array[] = new Array(9);
  for (let c = 0; c < 3; c++) {
    for (let col = 0; col < 3; col++) {
      const k = M[col * 3 + c]!;
      const t = new Float64Array(LUT_SIZE);
      for (let v = 0; v < LUT_SIZE; v++) t[v] = k * v;
      tables[c * 3 + col] = t;
    }
  }
  return tables;
}

/**
 * Evaluate one pixel through nine tables plus the offset.
 * Returns the unclamped result, matching the direct path.
 */
export function applyLutPixel(tables: Float64Array[], b: ArrayLike<number>, r: number, g: number, bl: number, out?: Vec3): Vec3 {
  const o = out ?? new Float64Array(3);
  o[0] = tables[0]![r]! + tables[1]![g]! + tables[2]![bl]! + b[0]!;
  o[1] = tables[3]![r]! + tables[4]![g]! + tables[5]![bl]! + b[1]!;
  o[2] = tables[6]![r]! + tables[7]![g]! + tables[8]![bl]! + b[2]!;
  return o;
}

/** The tables as a flat `Float64Array` of 2304 entries: channel-major `[c*3+col]*256+v`. */
export function flattenLutTables(tables: Float64Array[]): Float64Array {
  const flat = new Float64Array(9 * LUT_SIZE);
  for (let i = 0; i < 9; i++) flat.set(tables[i]!, i * LUT_SIZE);
  return flat;
}

// ---------------------------------------------------------------------------
// T3b: 3D LUT for nonlinear transforms
// ---------------------------------------------------------------------------

/** A function evaluated at a normalised RGB triple in `0..1`. */
export type Lut3dFn = (r: number, g: number, b: number) => Vec3 | readonly number[];

/**
 * A `size`-cubed table of output triples, indexed `((r*N + g)*N + b) * 3 + c`.
 *
 * The samples are stored as **8-bit** bytes quantised over `min..max`. At
 * 32x32x32 that is 98304 bytes — the "98 KB" of the plan, and the "quantised"
 * part of hypothesis T3b. A `Float32Array` would cost 384 KB to buy at most one
 * part in 255 of extra precision, and the whole pipeline outputs 8 bits.
 */
export interface Lut3d {
  size: number;
  /** Interleaved RGB samples, 0..255. Dequantise with `min` and `max`. */
  data: Uint8Array;
  /** The value that byte 0 represents, per channel. */
  min: Vec3;
  /** The value that byte 255 represents, per channel. */
  max: Vec3;
}

/**
 * Sample `fn` over a `size`-cubed grid of normalised inputs and pack the
 * results into an 8-bit table.
 *
 * `fn` should return a triple in `0..1`. The observed range is recorded in
 * `min`/`max`, so a function that returns something else is still recoverable.
 */
export function build3dLut(fn: Lut3dFn, size = LUT3D_SIZE): Lut3d {
  const n = size;
  const count = n * n * n;
  const raw = new Float32Array(count * 3);
  let minR = Infinity;
  let minG = Infinity;
  let minB = Infinity;
  let maxR = -Infinity;
  let maxG = -Infinity;
  let maxB = -Infinity;

  let i = 0;
  for (let ri = 0; ri < n; ri++) {
    const r = n === 1 ? 0 : ri / (n - 1);
    for (let gi = 0; gi < n; gi++) {
      const g = n === 1 ? 0 : gi / (n - 1);
      for (let bi = 0; bi < n; bi++) {
        const b = n === 1 ? 0 : bi / (n - 1);
        const out = fn(r, g, b);
        const x = out[0]!;
        const y = out[1]!;
        const z = out[2]!;
        raw[i++] = x;
        raw[i++] = y;
        raw[i++] = z;
        if (x < minR) minR = x;
        if (y < minG) minG = y;
        if (z < minB) minB = z;
        if (x > maxR) maxR = x;
        if (y > maxG) maxG = y;
        if (z > maxB) maxB = z;
      }
    }
  }

  const min = Float64Array.of(
    Number.isFinite(minR) ? minR : 0,
    Number.isFinite(minG) ? minG : 0,
    Number.isFinite(minB) ? minB : 0,
  );
  const max = Float64Array.of(
    Number.isFinite(maxR) ? maxR : 1,
    Number.isFinite(maxG) ? maxG : 1,
    Number.isFinite(maxB) ? maxB : 1,
  );

  const data = new Uint8Array(count * 3);
  for (let k = 0; k < count; k++) {
    for (let c = 0; c < 3; c++) {
      const span = max[c]! - min[c]!;
      const v = raw[k * 3 + c]!;
      const norm = span > 0 ? (v - min[c]!) / span : 0;
      data[k * 3 + c] = Math.round(clamp01(norm) * 255);
    }
  }

  return { size: n, data, min, max };
}

/** Dequantise one stored byte back to a real value. */
export function lut3dValue(lut: Lut3d, index: number, channel: number): number {
  const lo = lut.min[channel]!;
  const hi = lut.max[channel]!;
  return lo + ((hi - lo) * lut.data[index * 3 + channel]!) / 255;
}

/**
 * Trilinear sample of a 3D LUT at a normalised input.
 * Inputs are clamped to `0..1`.
 */
export function sample3dLut(lut: Lut3d, r: number, g: number, b: number, out?: Vec3): Vec3 {
  const n = lut.size;
  const o = out ?? new Float64Array(3);
  const fr = clamp01(r) * (n - 1);
  const fg = clamp01(g) * (n - 1);
  const fb = clamp01(b) * (n - 1);

  const r0 = Math.floor(fr);
  const g0 = Math.floor(fg);
  const b0 = Math.floor(fb);
  const r1 = Math.min(r0 + 1, n - 1);
  const g1 = Math.min(g0 + 1, n - 1);
  const b1 = Math.min(b0 + 1, n - 1);
  const tr = fr - r0;
  const tg = fg - g0;
  const tb = fb - b0;

  const i000 = (r0 * n + g0) * n + b0;
  const i001 = (r0 * n + g0) * n + b1;
  const i010 = (r0 * n + g1) * n + b0;
  const i011 = (r0 * n + g1) * n + b1;
  const i100 = (r1 * n + g0) * n + b0;
  const i101 = (r1 * n + g0) * n + b1;
  const i110 = (r1 * n + g1) * n + b0;
  const i111 = (r1 * n + g1) * n + b1;

  for (let c = 0; c < 3; c++) {
    const c000 = lut3dValue(lut, i000, c);
    const c001 = lut3dValue(lut, i001, c);
    const c010 = lut3dValue(lut, i010, c);
    const c011 = lut3dValue(lut, i011, c);
    const c100 = lut3dValue(lut, i100, c);
    const c101 = lut3dValue(lut, i101, c);
    const c110 = lut3dValue(lut, i110, c);
    const c111 = lut3dValue(lut, i111, c);

    const c00 = c000 + (c001 - c000) * tb;
    const c01 = c010 + (c011 - c010) * tb;
    const c10 = c100 + (c101 - c100) * tb;
    const c11 = c110 + (c111 - c110) * tb;
    const c0 = c00 + (c01 - c00) * tg;
    const c1 = c10 + (c11 - c10) * tg;
    o[c] = c0 + (c1 - c0) * tr;
  }
  return o;
}

/** Bytes of a 3D LUT's backing store. 32-cubed gives 98304: the plan's 98 KB. */
export function lut3dBytes(lut: Lut3d): number {
  return lut.data.byteLength;
}

function clamp01(v: number): number {
  if (!(v > 0)) return 0;
  return v > 1 ? 1 : v;
}

/**
 * Pack the nine axis tables into a GLSL-friendly `Float32Array` for upload as a
 * 2D texture of size 256 x 9. Row `i` holds table `i`.
 */
export function lutTablesTexture(tables: Float64Array[]): Float32Array {
  const tex = new Float32Array(9 * LUT_SIZE);
  for (let i = 0; i < 9; i++) {
    const src = tables[i]!;
    for (let v = 0; v < LUT_SIZE; v++) tex[i * LUT_SIZE + v] = src[v]!;
  }
  return tex;
}

